import React from 'react';
import { Typography } from '@mui/material';
import { getCriteriaVariableLabel } from '../../../pages/ViewGoal/Insights/Criteria/utils/criteriaUtils';

export const isBehaviorType = (goalData) =>
  goalData?.GoalDetails?.TypeOfGoal?.Type === 'Behavior';

export const isSkillType = (goalData) =>
  goalData?.GoalDetails?.TypeOfGoal?.Type === 'Skill Acquisition' ||
  goalData?.GoalDetails?.TypeOfGoal?.Type === 'Parent Training';

export const formatDate = (dateString) => {
  if (!dateString || dateString === '-') return null;
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString();
};

export const getTeacherName = (teachersForStudent, id) => {
  const teacher = teachersForStudent?.find((t) => t.uid === id);
  return teacher?.info?.fullname || 'Unknown';
};

export const formatPromptingLevel = (pl) => {
  if (!pl) return '';
  const plKey = String(pl).toLowerCase();
  const promptingLevelLabels = {
    'physical-full-total-assist': 'Physical → Full → Total Assist (75+% effort)',
    'physical-full-maximal-assist': 'Physical → Full → Maximal Assist',
    'physical-partial-moderate': 'Physical → Partial → Moderate',
    'physical-partial-minimal': 'Physical → Partial → Minimal',
    'verbal-minimal': 'Verbal → Minimal',
    'verbal-moderate': 'Verbal → Moderate',
    'verbal-maximum': 'Verbal → Maximum',
    'permanent-visual': 'Permanent Visual Prompting',
    'permanent-visual-prompting': 'Permanent Visual Prompting',
    visual: 'Visual Prompting',
    'gestural-prompting': 'Gestural Prompting',
    modeling: 'Modeling',
    'positional-prompting': 'Positional Prompting',
    independent: 'Independent',
  };

  if (promptingLevelLabels[plKey]) return promptingLevelLabels[plKey];

  return plKey
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const renderGoalDetails = (goalData) => {
  const antecedent =
    goalData?.DataCollection?.antecedent?.Antecedent ||
    goalData?.dataCollection?.DataCollection?.antecedent?.Antecedent;
  const targetBehavior =
    goalData?.DataCollection?.targetBehavior?.TargetBehavior ||
    goalData?.dataCollection?.DataCollection?.targetBehavior?.TargetBehavior;
  const byDate =
    goalData?.DataCollection?.ByDate ||
    goalData?.dataCollection?.DataCollection?.ByDate ||
    '-';
  const subgoals =
    goalData?.HasSubgoal?.Subgoals ||
    goalData?.dataCollection?.HasSubgoal?.Subgoals ||
    [];
  const measurementType =
    goalData?.dataCollection?.DataCollection?.measurementType?.Type ||
    goalData?.DataCollection?.measurementType?.Type;
  const measurementUnit =
    measurementType === 'Percentage'
      ? '%'
      : ['Duration', 'Frequency'].includes(measurementType)
      ? 'times'
      : '';
  const dataType =
    goalData?.dataCollection?.DataCollection?.measurementType?.DataType ||
    goalData?.DataCollection?.measurementType?.DataType ||
    '';
  const trialsMin =
    goalData?.dataCollection?.DataCollection?.measurementType?.TrailsValue?.TrailsMin ||
    goalData?.DataCollection?.measurementType?.TrailsValue?.TrailsMin;
  const trialsMax =
    goalData?.dataCollection?.DataCollection?.measurementType?.TrailsValue?.TrailsMax ||
    goalData?.DataCollection?.measurementType?.TrailsValue?.TrailsMax;

  const currentSubgoal = subgoals.find((subgoal) => subgoal.CurrentGoal === 'Yes');

  if (currentSubgoal) {
    const target =
      currentSubgoal?.Target === 'Atleast'
        ? 'at least'
        : currentSubgoal?.Target === 'Atmost'
        ? 'at most'
        : currentSubgoal?.Target?.toLowerCase();
    const targetDuration = currentSubgoal?.Measurement;
    const consecutiveTarget = currentSubgoal?.ConsecutiveTargetDuration ? 'consecutive' : '';
    const consecutiveTrials = currentSubgoal?.ConsecutiveTrialDuration ? 'consecutive trials' : '';

    return (
      <Typography sx={{ fontFamily: 'Lato', fontSize: '1.1rem', lineHeight: 1.6 }}>
        {antecedent} {targetBehavior} {target} {targetDuration}{' '}
        {consecutiveTarget} {measurementUnit}{' '}
        {measurementType === 'Trials' && (
          <>
            {dataType} in {trialsMin} {consecutiveTrials} out of {trialsMax} trials{' '}
          </>
        )}
        by {byDate}.
      </Typography>
    );
  }

  const targetValue =
    goalData?.DataCollection?.measurementType?.Target ||
    goalData?.dataCollection?.DataCollection?.measurementType?.Target;

  const target =
    targetValue === 'Atleast'
      ? 'at least'
      : targetValue === 'Atmost'
      ? 'at most'
      : targetValue?.toLowerCase() || '';

  const targetDuration =
    goalData?.DataCollection?.measurementType?.TargetDuration ||
    goalData?.dataCollection?.DataCollection?.measurementType?.TargetDuration ||
    '';

  const consecutiveTarget =
    goalData?.DataCollection?.measurementType?.ConsecutiveTargetDuration ||
    goalData?.dataCollection?.DataCollection?.measurementType?.ConsecutiveTargetDuration
      ? 'consecutive'
      : '';

  const consecutiveTrials =
    goalData?.DataCollection?.measurementType?.ConsecutiveTrialDuration ||
    goalData?.dataCollection?.DataCollection?.measurementType?.ConsecutiveTrialDuration
      ? 'consecutive trials'
      : '';

  const criteria =
    goalData?.dataCollection?.DataCollection?.measurementType?.Criteria ||
    goalData?.DataCollection?.measurementType?.Criteria;

  return (
    <Typography sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
      {antecedent}, {targetBehavior} {target} {targetDuration} {consecutiveTarget} {measurementUnit}{' '}
      {measurementType === 'Trials' && (
        <>
          {dataType} in {trialsMin} {consecutiveTrials} out of {trialsMax} trials{' '}
        </>
      )}
      {criteria?.map((item, index) => {
        const criteriaMeasurementUnit =
          item.MeasurementType === 'Percentage'
            ? '%'
            : ['Duration', 'Frequency'].includes(item.MeasurementType)
            ? 'times'
            : '';
        const targetText =
          item.Target === 'Atmost'
            ? 'at most'
            : item.Target === 'Atleast'
            ? 'at least'
            : item?.Target?.toLowerCase();

        return (
          <span key={index}>
            {' '}
            {index !== 0 && criteria[index - 1]?.MeasurementType !== 'Percentage' && 'with '}
            {index !== 0 && criteria[index - 1]?.MeasurementType === 'Percentage' && 'in '}
            {index === 0 && measurementType !== 'Percentage' && 'with '}
            {index === 0 && measurementType === 'Percentage' && 'in '}
            {targetText} {item.TargetDuration}{' '}
            {item.Consecutive && 'consecutive '}
            <>
              {criteriaMeasurementUnit && `${criteriaMeasurementUnit} of `}
              {getCriteriaVariableLabel(item)}
            </>
          </span>
        );
      })}{' '}
      by {byDate}.
    </Typography>
  );
};


